import type { GuildRole } from '@/types/api';
import { THEME_IS_LIGHT, type Theme } from '@/lib/theme';

/**
 * Discord-style: a member's display color comes from their HIGHEST
 * positioned role that has a non-zero color set. Roles without a color
 * (color === 0, "default") are skipped — that's why creating a role
 * with no color doesn't accidentally turn a member's name grey.
 *
 * Returns a `#rrggbb` string ready for inline `style={{ color }}` or
 * null if the member should render in the default ink color.
 */
export function resolveRoleColor(
  roleIds: string[] | undefined,
  rolesById: Map<string, GuildRole>,
): string | null {
  if (!roleIds || roleIds.length === 0) return null;
  let best: GuildRole | null = null;
  for (const id of roleIds) {
    const role = rolesById.get(id);
    if (!role || role.color === 0) continue;
    if (!best || role.position > best.position) best = role;
  }
  if (!best) return null;
  return `#${best.color.toString(16).padStart(6, '0')}`;
}

/** Build a lookup map once per render rather than scanning the roles
 * array N times. Callers typically have `roles: GuildRole[]` from
 * `api.getGuild()` and want to color a list of members. */
export function indexRolesById(roles: GuildRole[]): Map<string, GuildRole> {
  return new Map(roles.map((r) => [r.id, r]));
}

/**
 * Clamp a user-picked role color so it stays legible against the active
 * theme's background, keeping the chosen hue intent.
 *
 * Role colors frequently land at the extremes. On the dark theme, deep
 * navy / maroon / forest disappear against the near-black ground, so we
 * mix them toward white. On the light theme the opposite happens: pale
 * picks (and the auto-lightened defaults like blurple) wash out against
 * a white ground, so we mix them toward black instead. We use a rec. 709
 * luminance approximation to decide which side a color falls on.
 *
 * Returns the input unchanged if already legible, an adjusted `#rrggbb`
 * otherwise. Pass `null` through.
 */
export function clampRoleColor(hex: string | null, theme: Theme): string | null {
  if (!hex) return null;
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const raw = m[1]!;
  let r = parseInt(raw.slice(0, 2), 16);
  let g = parseInt(raw.slice(2, 4), 16);
  let b = parseInt(raw.slice(4, 6), 16);
  // Perceived luminance (rec. 709 approximation, fast enough for render).
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  if (THEME_IS_LIGHT[theme]) {
    // Darken too-light colors so they stay legible on a white-ish ground.
    const CEIL = 0.62;
    if (lum <= CEIL) return `#${raw}`;
    const mix = Math.min(0.6, (lum - CEIL) / (1 - CEIL));
    r = Math.round(r * (1 - mix));
    g = Math.round(g * (1 - mix));
    b = Math.round(b * (1 - mix));
  } else {
    // Brighten too-dark colors so they stay legible on a near-black ground.
    const FLOOR = 0.45;
    if (lum >= FLOOR) return `#${raw}`;
    const mix = Math.min(0.6, (FLOOR - lum) / FLOOR);
    r = Math.round(r + (255 - r) * mix);
    g = Math.round(g + (255 - g) * mix);
    b = Math.round(b + (255 - b) * mix);
  }
  return (
    '#' +
    r.toString(16).padStart(2, '0') +
    g.toString(16).padStart(2, '0') +
    b.toString(16).padStart(2, '0')
  );
}
